import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface GenerationResult {
  moduleId: string;
  title: string;
  status: 'success' | 'error';
  contentSections?: number;
  quizQuestions?: number;
  error?: string;
}

export const AutoContentGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [currentModule, setCurrentModule] = useState<string>('');
  const { toast } = useToast();

  const generateAllContent = async () => {
    setIsGenerating(true);
    setProgress(0);
    setResults([]);
    setCurrentModule('');

    try {
      toast({
        title: "Ξεκινά η δημιουργία περιεχομένου",
        description: "Αναζητώ πληροφορίες στο διαδίκτυο και δημιουργώ εκπαιδευτικό υλικό...",
      });

      const { data, error } = await supabase.functions.invoke('generate-training-content');

      if (error) {
        console.error('Error calling edge function:', error);
        toast({
          title: "Σφάλμα",
          description: "Απέτυχε η κλήση της υπηρεσίας δημιουργίας περιεχομένου",
          variant: "destructive",
        });
        return;
      }

      if (data && data.results) {
        setResults(data.results);
        const successCount = data.results.filter((r: GenerationResult) => r.status === 'success').length;
        const totalCount = data.results.length;
        
        toast({
          title: "Ολοκληρώθηκε η δημιουργία",
          description: `Επιτυχής δημιουργία περιεχομένου για ${successCount}/${totalCount} εκπαιδεύσεις`,
        });
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε σφάλμα κατά τη δημιουργία περιεχομένου",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setProgress(100);
    }
  };

  const getStatusIcon = (status: 'success' | 'error' | 'generating') => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'generating':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
    }
  };

  const getStatusColor = (status: 'success' | 'error') => {
    switch (status) {
      case 'success':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'error':
        return 'bg-red-50 text-red-700 border-red-200';
    }
  };

  // Auto-generate content on component mount
  useEffect(() => {
    generateAllContent();
  }, []);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Αυτόματη Δημιουργία Εκπαιδευτικού Περιεχομένου</h1>
        <p className="text-muted-foreground">
          Δημιουργία περιεχομένου για όλες τις εκπαιδεύσεις με χρήση τεχνητής νοημοσύνης
        </p>
      </div>

      {/* Content Generation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className={`h-5 w-5 ${isGenerating ? 'animate-spin' : ''}`} />
            Δημιουργία Περιεχομένου
          </CardTitle>
          <CardDescription>
            Αναζήτηση υλικού στο διαδίκτυο, δημιουργία μαθημάτων και κουίζ
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isGenerating && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Πρόοδος:</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="w-full" />
              {currentModule && (
                <p className="text-sm text-muted-foreground">
                  Επεξεργασία: {currentModule}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-4">
            <Button
              onClick={generateAllContent}
              disabled={isGenerating}
              className="flex-1"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Δημιουργία σε εξέλιξη...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Επανεκκίνηση Δημιουργίας
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Generation Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Αποτελέσματα Δημιουργίας</CardTitle>
            <CardDescription>
              Περίληψη της δημιουργίας περιεχομένου για κάθε εκπαίδευση
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.map((result) => (
                <div
                  key={result.moduleId}
                  className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(result.status)}
                      <div>
                        <h4 className="font-medium">{result.title}</h4>
                        {result.status === 'success' && result.contentSections && (
                          <p className="text-sm mt-1">
                            Δημιουργήθηκαν {result.contentSections} τμήματα περιεχομένου
                            {result.quizQuestions && ` και ${result.quizQuestions} ερωτήσεις κουίζ`}
                          </p>
                        )}
                        {result.status === 'error' && result.error && (
                          <p className="text-sm mt-1 text-red-600">{result.error}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant={result.status === 'success' ? 'default' : 'destructive'}>
                      {result.status === 'success' ? 'Επιτυχία' : 'Σφάλμα'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">Περίληψη</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {results.filter(r => r.status === 'success').length}
                  </div>
                  <div className="text-muted-foreground">Επιτυχείς</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {results.filter(r => r.status === 'error').length}
                  </div>
                  <div className="text-muted-foreground">Αποτυχίες</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {results.reduce((acc, r) => acc + (r.contentSections || 0), 0)}
                  </div>
                  <div className="text-muted-foreground">Συνολικά Τμήματα</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How it Works */}
      <Card>
        <CardHeader>
          <CardTitle>Πώς Λειτουργεί</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <h4 className="font-medium">Αναζήτηση Υλικού</h4>
              <p className="text-sm text-muted-foreground">
                Χρήση Perplexity AI για αναζήτηση τελευταίων πληροφοριών στο διαδίκτυο
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-green-600 font-bold">2</span>
              </div>
              <h4 className="font-medium">Δημιουργία Περιεχομένου</h4>
              <p className="text-sm text-muted-foreground">
                OpenAI GPT-4 δημιουργεί δομημένα μαθήματα και ασκήσεις στα ελληνικά και αγγλικά
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <h4 className="font-medium">Δημιουργία Κουίζ</h4>
              <p className="text-sm text-muted-foreground">
                Αυτόματη δημιουργία ερωτήσεων κατανόησης με βάση το περιεχόμενο
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};